<template>
  <div v-if="guide">
    <div class="container bg-white mx-auto p-0">
        <Hero
            breadcrumbCategoryTitle = "Learn"
            breadcrumbCategoryLink = "/learn"
            breadcrumbSubcategoryTitle = "Lawn Care Guides"
            breadcrumbSubcategoryLink = "/learn/lawn-care-guides"
            :breadcrumbTitle = "guide.title"
            :breadcrumbLink = "'/learn/lawn-care-guides/' + guide.slug"
            :heroImageLink = undefined
            :title = "guide.title"
            :text = "guide.description2"
            color = "Green"
            isBasicImage = "Y"
        />
    </div>

    <div class="relative">
      <div class="container bg-white mx-auto pt-10">
        <ServicesCardList
          :services="services"
          size="medium"
        />
        <div class="grid lg:grid-cols-1">
          <ClientOnly>
            <div v-html="guide.description"></div>
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { $sg, $config, $services, $userContext } = useNuxtApp();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();

const slug = computed(() => route.params.slug as string);
const imgLocation = $config.public.sgCmsImage;
const frontEndUrl = runtimeConfig.public?.frontEndUrl || "https://www.spring-green.com";

const services = ref<any>(null);

// SSR-friendly data fetch for guide
const { data: guideData } = await useAsyncData(
  `lawn-guide-${slug.value}`,
  () => $sg.getOctoberData(`learn/${slug.value}/lawn`),
  { watch: [slug] }
);

// Handle redirects and not found
watchEffect(() => {
  if (!guideData.value || guideData.value.length === 0) {
    navigateTo('/learn/lawn-care-guides');
    return;
  }
  if (guideData.value[0]?.redirect_url) {
    navigateTo(`/learn/lawn-care-guides/${guideData.value[0].redirect_url}`);
  }
});

const guide = computed(() => guideData.value?.[0] ?? null);

const featuredImage = computed(() =>
  guide.value?.featured_image ? `${imgLocation}${guide.value.featured_image}` : undefined
);

const socialImage = computed(() =>
  guide.value?.social_image ? `${imgLocation}${guide.value.social_image}` : undefined
);

const guideUrl = computed(() => `${frontEndUrl}/learn/lawn-care-guides/${slug.value}/`);

// Build Article structured data
const articleSD = computed(() => {
  if (!guide.value) return null;
  return buildArticleSchema({
    headline: guide.value.title,
    description: guide.value.meta_description || guide.value.excerpt,
    datePublished: guide.value.published_at,
    dateModified: guide.value.updated_at,
    image: socialImage.value || featuredImage.value,
    articleSection: "Lawn Care",
    url: guideUrl.value,
  });
});

// Apply SEO head
watchEffect(() => {
  if (!guide.value) return;

  const description = guide.value.meta_description || guide.value.excerpt;

  useSeoHead({
    title: guide.value.seo_title || guide.value.title || "Lawn Care Guide | Spring-Green",
    description: description ||
      "Expert lawn care tips and guides from Spring-Green professionals.",
    openGraph: {
      title: guide.value.social_title || guide.value.seo_title || guide.value.title,
      description: guide.value.social_description || description,
      image: socialImage.value || featuredImage.value,
      url: guideUrl.value,
      type: "article",
    },
    twitter: {
      title: guide.value.social_title || guide.value.seo_title || guide.value.title,
      description: guide.value.social_description || description,
      image: socialImage.value || featuredImage.value,
      card: socialImage.value || featuredImage.value ? "summary_large_image" : "summary",
    },
    structuredData: [articleSD.value].filter(Boolean),
    breadcrumbs: [
      { name: "Home", url: `${frontEndUrl}/` },
      { name: "Learn", url: `${frontEndUrl}/learn/` },
      { name: "Lawn Care Guides", url: `${frontEndUrl}/learn/lawn-care-guides/` },
      { name: guide.value.title, url: guideUrl.value },
    ],
  });
});

onMounted(async () => {
  // Client-side refresh for user-specific context
  await $userContext.metaRefresh({
    refresh: new Set(["ORDER_INFO", "SERVICE_INFO", "PAYMENT_INFO"]),
  });

  // Load connected services client-side
  if (guide.value?.connected_services) {
    const serviceNames = guide.value.connected_services.map((service: any) => service.name);
    services.value = $services.getServicesByName(serviceNames);
  }
});
</script>

<style scoped>
.hero-top {
  border-bottom-left-radius: 70px;
  background-position: right top;
  background-size: 804px auto;
  background-repeat: no-repeat;
  height: 470px;
  padding: 60px 0;
  border-bottom-left-radius: 70px;
  background-size: auto 100%;
}

.hero-bottom {
  background-size: 804px auto;
  background-position: right -470px;
  height: 70px;
}

.mh-700 {
  min-height: 700px;
}

@media (min-width: 768px) {
    .right-mini-curve {
        position: relative !important;
    }
}
</style>
